<template>
  <section>
    <div :class="$theme.isDefault ? 'primary lighten-1' : 'secondary lighten-1'" class="pb-6">
      <v-container>
        <breadcrumbs v-if="$theme.isDefault"/>
        <nuxt-link
          :to="{name: 'shop-category', params: {category: $route.params.category}}"
          :class="$theme.isDefault ? 'accent--text' : 'primary--text'"
          class="caption no-line"
        >
          <v-btn fab color="accent" class="mr-2">
            <font-awesome-icon :icon="['far', 'arrow-left']"/>
          </v-btn>
          Terug
        </nuxt-link>
      </v-container>
    </div>
    <v-container>
      <v-row>
        <v-col cols="12" sm="4">
          <product-images :images="productImages"/>
        </v-col>
        <v-col cols="12" sm="8">
          <h1 :class="$theme.isDefault ? 'blue--text' : 'primary--text text--lighten-2'">
            {{ product.title }}
          </h1>
          <dynamic-text v-if="selectedVariant.shopText" class="pb-6" :html="selectedVariant.shopText"/>
          <dynamic-text v-else class="pb-6" :html="product.text"/>

          <v-btn v-if="product.videos.length > 0" @click="openDialog">Bekijk productvideo</v-btn>

          <template v-if="!selectedVariant || !selectedVariant.price_on_request">
            <div v-if="isBundleProduct" class="display-2">Prijs vanaf €{{ lowestOptionPrice | currency }}</div>
            <div v-if="selectedVariant && selectedVariant.monthly">
              Bij dit product is het mogelijk om per maand te betalen. Het maandelijkse bedrag is
              €{{ monthlyPrice | currency }}.
            </div>
          </template>

          <template v-if="isConfigurable && product.variants.length > 1">
            <div class="caption mt-12">
              {{ product.variant_select_label || 'Selecteer een variant:' }}
              <button v-if="selectedVariant.license_terms" @click="viewLicenceTermsDialog = true">
                <font-awesome-icon :icon="['fa', 'info-circle']"/>
              </button>
            </div>
            <v-dialog
              v-model="viewLicenceTermsDialog"
              width="40vw"
            >
              <v-card class="pa-4 pa-sm-12" :color="$vuetify.theme.themes.light.secondary.lighten6">
                <v-card-text>
                  <div v-html="selectedVariant.license_terms"/>
                </v-card-text>
              </v-card>
            </v-dialog>
            <template v-if="product.alternative_variant_select">
              <div class="mt-4">
                <alternative-select-item
                  v-for="variant in product.variants"
                  :key="variant.id"
                  :variant="variant"
                  :selected="selectedVariant === variant"
                  @select="selectedVariant = variant"
                />
              </div>
            </template>
            <template v-else>
              <v-select
                v-model="selectedVariant"
                return-object
                :items="product.variants"
                item-text="title"
              />
            </template>
          </template>
          <template v-if="possibleOptions.length > 0">
            <div class="caption mt-12">Opties:</div>
            <v-checkbox
              v-for="option in possibleOptions"
              :key="option.title"
              v-model="options"
              :value="option"
              class="check mt-1"
            >
              <template v-slot:label>
                {{ option.title }} - <span class="font-weight-bold ml-2">€{{ option.price_incl | currency }}<span
                  v-if="option.monthly"
                > / €{{ option.monthly_incl | currency }} per maand</span></span>
              </template>
            </v-checkbox>
          </template>
          <v-divider class="navy"/>
          <template v-if="product.training">
            <product-training v-model="selectedTraining" class="mt-12" :training="product.training"/>
          </template>

          <v-row v-if="selectedVariant && selectedVariant.extra_field">
            <v-col>
              <div class="caption mb-3">Aanvullende gegevens:</div>
              <v-text-field v-model="extraField" :label="`${selectedVariant.extra_field} *`" rounded outlined/>
            </v-col>
          </v-row>

          <v-row v-if="!selectedVariant || !selectedVariant.price_on_request">
            <v-col cols="12" sm="7" class="display-4">
              <div>
                <div v-if="monthly">
                  <p>€{{ monthlyPrice | currency }} per maand <span class="caption">(€{{ monthlyPrice * 12 | currency }} per jaar)</span></p>
                  <p class="caption">Eerste betaling: €{{ firstPaymentAmount | currency }} {{ firstPaymentSpecification }}</p>
                </div>
                <div v-else>
                  <p>
                    €{{ subTotal | currency }}
                    {{ product.is_subscription && !trainingSubTotal ? 'per jaar' : '' }}
                  </p>
                  <p class="caption">
                    <template v-if="product.is_subscription && trainingSubTotal">
                      Abonnement: €{{ subTotal - trainingSubTotal | currency }} per jaar<br>
                    </template>
                    <template v-else-if="trainingSubTotal">
                      Product: €{{ subTotal - trainingSubTotal | currency }} eenmalig<br>
                    </template>
                    <template v-if="trainingSubTotal">
                      Training: €{{ trainingSubTotal | currency }} eenmalig
                    </template>
                  </p>
                </div>
              </div>
              <v-checkbox v-if="hasMonthly" v-model="monthly" class="check mt-1">
                <template v-slot:label>
                  Maandelijkse betaling van €{{ monthlyPrice | currency }}
                </template>
              </v-checkbox>
            </v-col>
            <v-col cols="4" sm="2">
              <v-text-field v-model="quantity" type="number" min="1" rounded outlined/>
            </v-col>
            <v-btn
              :disabled="!canOrder"
              rounded
              color="accent"
              class="mt-4"
              @click="addToCart"
            >
              In winkelwagen
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <variant-details v-if="isConfigurable" :variant="selectedVariant" :product="product"/>
    <product-dialog
      v-for="video in product.videos"
      :key="video.title"
      v-model="dialog"
      :video="video"
    />
  </section>
</template>

<script>
import BorderHeader from '~/components/header/BorderHeader';
import VariantDetails from '~/components/shop/VariantDetails';
import ECommerceGtm from '~/plugins/mixins/e-commerce-gtm';
import CompareTable from '~/components/shop/CompareTable';
import ProductImages from '~/components/shop/ProductImages';
import ProductTraining from '~/components/shop/ProductTraining';
import Breadcrumbs from '~/components/shop/Breadcrumbs';
import AlternativeSelectItem from '~/components/shop/AlternativeSelectItem';
import ProductDialog from '~/components/shop/ProductDialog.vue';

export default {
  components: {
    Breadcrumbs,
    CompareTable,
    BorderHeader,
    VariantDetails,
    ProductImages,
    ProductDialog,
    ProductTraining,
    AlternativeSelectItem,
  },
  mixins: [ECommerceGtm],
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      options: [],
      quantity: 1,
      selectedVariant: {},
      monthly: false,
      selectedTraining: null,
      extraField: null,
      viewLicenceTermsDialog: false,
      dialog: false,
    };
  },
  computed: {
    optionSubTotal() {
      const optionPriceArray = this.options.map(option => option.price_incl);

      return optionPriceArray.reduce((a, b) => a + b, 0);
    },
    trainingSubTotal() {
      return this.selectedTraining ? this.selectedTraining.price_incl : 0;
    },
    firstPaymentAmount() {
      if (!this.monthly) {
        return null;
      }

      const optionSubTotal = this.optionSubTotal;
      const trainingsPrice = this.trainingSubTotal;

      return parseFloat(((this.monthlyPrice + optionSubTotal) * this.quantity) + trainingsPrice).toFixed(2);
    },
    firstPaymentSpecification() {
      const items = ['maand 1'];

      if (this.optionSubTotal) {
        items.push('opties');
      }

      if (this.trainingSubTotal) {
        items.push('training');
      }

      return items.length > 1 ? '(' + items.join(' + ') + ')' : null;
    },
    subTotal() {
      const optionSubTotal = this.optionSubTotal;
      const trainingsPrice = this.trainingSubTotal;
      const price = this.monthly ? this.monthlyPrice * 12 : this.productPrice;

      return parseFloat(((price + optionSubTotal) * this.quantity) + trainingsPrice).toFixed(2);
    },
    productPrice() {
      return this.isConfigurable ? this.selectedVariant.price_incl : this.product.price_incl;
    },
    productImages() {
      return this.isConfigurable ? this.selectedVariant.images : this.product.images;
    },
    possibleOptions() {
      return this.isConfigurable ? this.selectedVariant.options : this.product.options;
    },
    isConfigurable() {
      return this.product.is_configurable;
    },
    hasMonthly() {
      return this.product.is_subscription && ((this.selectedVariant && this.selectedVariant.monthly) || this.product.monthly);
    },
    monthlyPrice() {
      return this.isConfigurable ? this.selectedVariant.monthly_incl : this.product.monthly_incl;
    },
    isBundleProduct() {
      return !this.product.is_configurable && this.productPrice === 0;
    },
    lowestOptionPrice() {
      const optionPrices = this.product.options.map(option => option.price_incl);
      return Math.min([...optionPrices, 0]);
    },
    canOrder() {
      if (this.selectedVariant && this.selectedVariant.extra_field && !this.extraField) {
        return false;
      }

      if (this.isBundleProduct) {
        return this.options.length > 0 || this.selectedTraining;
      }

      return true;
    },
  },
  watch: {
    selectedVariant: {
      deep: true,
      handler() {
        this.options = [];
      },
    },
  },
  created() {
    this.initializeActiveVariant();
  },
  methods: {
    openDialog() {
      this.dialog = true;
    },
    initializeActiveVariant() {
      if (this.isConfigurable) {
        this.selectedVariant = this.product.variants[0];

        if (this.$route.query.variant) {
          const found = this.product.variants.filter(variant => variant.title === this.$route.query.variant);
          this.selectedVariant = found.length > 0 ? found[0] : this.product.variants[0];
        }
      }
    },
    setActiveVariant(val) {
      const variant = this.product.variants.filter(variant => variant.title === val);
      if (variant.length > 0) {
        this.selectedVariant = variant[0];
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
      } else {
        this.$store.commit('notify/error', `${val} is niet beschikbaar`);
      }
    },
    addToCart() {
      const product = this.product;
      const selectedOptions = this.options;

      let payload = {
        id: product.id,
        title: product.title,
        is_subscription: product.is_subscription,
        weight: product.weight,
        type: product.type,
        permalink: product.permalink,
        category: this.$route.params.category,
        training: this.selectedTraining,
        options: selectedOptions,
        quantity: parseInt(this.quantity),
        product_total: parseFloat(this.subTotal),
        monthly: this.monthly,
      };

      const uniqIdSuffix =
        '|' + (this.selectedTraining ? this.selectedTraining.id + ',' + this.selectedTraining.dateId : '') +
        '|' + selectedOptions.map(o => o.id).join(',');

      if (this.isConfigurable) {
        payload = {
          ...payload,
          uniqid: product.id + ',' + this.selectedVariant.id + uniqIdSuffix,
          variant_title: this.selectedVariant.title,
          variant: this.selectedVariant.id,
          image: this.selectedVariant.image,
          price_excl: this.monthly ? this.selectedVariant.monthly_excl * 12 : this.selectedVariant.price_excl,
          price_incl: this.monthly ? this.selectedVariant.monthly_incl * 12 : this.selectedVariant.price_incl,
          monthly_excl: this.monthly ? this.selectedVariant.monthly_excl : 0,
          monthly_incl: this.monthly ? this.selectedVariant.monthly_incl : 0,
          tax_group: this.selectedVariant.tax_group,
          extra_field: this.selectedVariant.extra_field ? this.extraField : null,
        };
      } else {
        payload = {
          ...payload,
          uniqid: product.id + uniqIdSuffix,
          image: product.image,
          price_excl: this.monthly ? product.monthly_excl * 12 : product.price_excl,
          price_incl: this.monthly ? product.monthly_incl * 12 : product.price_incl,
          monthly_excl: this.monthly ? product.monthly_excl : 0,
          monthly_incl: this.monthly ? product.monthly_incl : 0,
          tax_group: product.tax_group,
        };
      }

      this.$store.commit('cart/ADD_PRODUCT_TO_CART', payload);
      this.gtmPushAddToCart(payload);
      this.$store.commit('notify/success', `${product.title} is toegevoegd aan uw winkelwagen.`);
    },
  },
};
</script>
